import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, orderBy, query, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import { User } from '../../../types';
import { getCurriculumAdapterById } from '../../curriculum/adapters/registry';

const FeedbackViewer: React.FC = () => {
  const [feedback, setFeedback] = useState<any[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ teacher: '', squad: '', lessonId: '' });
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteMessage, setDeleteMessage] = useState<string>('');
  
  // Confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{
    feedbackId: string;
    teacherName: string;
    squadName: string;
  } | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch feedback
        const feedbackRef = collection(db, 'classFeedback');
        const feedbackQuery = query(feedbackRef, orderBy('createdAt', 'desc'));
        const feedbackSnapshot = await getDocs(feedbackQuery);
        const feedbackData = feedbackSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setFeedback(feedbackData);

        // Fetch all users to map IDs to names
        const usersRef = collection(db, 'users');
        const usersSnapshot = await getDocs(usersRef);
        const usersData = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as User));
        setUsers(usersData);

      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load feedback data.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleDeleteClick = (feedbackId: string, teacherName: string, squadName: string) => {
    setPendingDelete({ feedbackId, teacherName, squadName });
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;

    const { feedbackId, teacherName, squadName } = pendingDelete;
    setDeletingId(feedbackId);
    setDeleteMessage('');
    setShowDeleteModal(false);

    try {
      // Delete from Firestore
      await deleteDoc(doc(db, 'classFeedback', feedbackId));
      
      // Update local state
      setFeedback(prevFeedback => prevFeedback.filter(item => item.id !== feedbackId));
      
      // Show success message
      setDeleteMessage(`✅ Feedback response from ${teacherName}'s ${squadName} deleted successfully`);
      
      // Clear success message after 3 seconds
      setTimeout(() => setDeleteMessage(''), 3000);
      
    } catch (err) {
      console.error("Error deleting feedback:", err);
      setDeleteMessage(`❌ Failed to delete feedback response: ${err instanceof Error ? err.message : 'Unknown error'}`);
      
      // Clear error message after 5 seconds
      setTimeout(() => setDeleteMessage(''), 5000);
    } finally {
      setDeletingId(null);
      setPendingDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setPendingDelete(null);
  };

  const filteredFeedback = useMemo(() => {
    return feedback
      .filter(item => {
        const teacherMatch = !filters.teacher || item.teacherName === filters.teacher;
        const squadMatch = !filters.squad || item.squadName === filters.squad;
        const lessonMatch = !filters.lessonId || String(item.lessonId || '') === String(filters.lessonId);
        return teacherMatch && squadMatch && lessonMatch;
      })
      .filter(item => {
        if (!searchTerm) return true;
        const lowerSearchTerm = searchTerm.toLowerCase();
        // Search across a few key fields
        return (
          item.teacherName?.toLowerCase().includes(lowerSearchTerm) ||
          item.squadName?.toLowerCase().includes(lowerSearchTerm) ||
          item.reflection?.toLowerCase().includes(lowerSearchTerm) ||
          item.improvementIdeas?.toLowerCase().includes(lowerSearchTerm)
        );
      });
  }, [feedback, searchTerm, filters]);
  
  const uniqueTeachers = useMemo(() => [...new Set(feedback.map(item => item.teacherName).filter(Boolean))], [feedback]);
  const uniqueSquads = useMemo(() => [...new Set(feedback.map(item => item.squadName).filter(Boolean))], [feedback]);
  const uniqueLessonIds = useMemo(
    () => [...new Set(feedback.map(item => item.lessonId).filter((v: any) => v !== undefined && v !== null && String(v).trim() !== ''))]
      .map(v => String(v))
      .sort((a, b) => {
        const na = Number(a);
        const nb = Number(b);
        if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
        return a.localeCompare(b);
      }),
    [feedback]
  );

  const getLessonLabel = (lessonId: string) => {
    try {
      const adapter = getCurriculumAdapterById('music');
      const level = adapter.getLevelById(String(lessonId)) as any;
      return level?.title || `Level ${lessonId}`;
    } catch {
      return `Level ${lessonId}`;
    }
  };

  const renderFieldValue = (key: string, value: any) => {
    if (key === 'teacherId') {
      const teacher = users.find(u => u.id === value);
      return teacher?.displayName || value;
    }
    if (key === 'lessonId') {
      // Without explicit curriculum in feedback, default to music adapter
      const adapter = getCurriculumAdapterById('music');
      const level = adapter.getLevelById(String(value));
      return (level as any)?.title || value;
    }
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (value && typeof value.toDate === 'function') return value.toDate().toLocaleString();
    if (typeof value === 'object' && value !== null) return <pre>{JSON.stringify(value, null, 2)}</pre>;
    return value || 'N/A';
  };

  if (loading) {
    return <div>Loading feedback...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="feedback-viewer-container">
      <h2>Class Feedback Responses ({filteredFeedback.length})</h2>
      
      {/* Delete Message Display */}
      {deleteMessage && (
        <div className={`admin-message ${deleteMessage.includes('❌') ? 'error' : 'success'}`}>
          {deleteMessage}
          <button onClick={() => setDeleteMessage('')} className="message-close">×</button>
        </div>
      )}
      
      <div className="filters-container">
        <input
          type="text"
          placeholder="Search feedback..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="admin-input"
        />
        <select value={filters.teacher} onChange={(e) => setFilters(f => ({ ...f, teacher: e.target.value }))} className="admin-select">
          <option value="">All Teachers</option>
          {uniqueTeachers.map(name => <option key={name} value={name}>{name}</option>)}
        </select>
        <select value={filters.squad} onChange={(e) => setFilters(f => ({ ...f, squad: e.target.value }))} className="admin-select">
          <option value="">All Squads</option>
          {uniqueSquads.map(name => <option key={name} value={name}>{name}</option>)}
        </select>
        <select
          value={filters.lessonId}
          onChange={(e) => setFilters(f => ({ ...f, lessonId: e.target.value }))}
          className="admin-select"
          title="Filter by lesson/level taught"
        >
          <option value="">All Levels</option>
          {uniqueLessonIds.map(id => (
            <option key={id} value={id}>
              {getLessonLabel(id)}
            </option>
          ))}
        </select>
      </div>
      <div className="feedback-list">
        {filteredFeedback.map(item => (
          <div key={item.id} className="feedback-card">
            <div className="feedback-card-header">
              <h3>{item.teacherName}'s {item.squadName || 'Unnamed Squad'}</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <small>{item.createdAt?.toDate().toLocaleString() || 'No Date'}</small>
                <button
                  onClick={() => handleDeleteClick(item.id, item.teacherName, item.squadName || 'Unnamed Squad')}
                  className="delete-button"
                  disabled={deletingId === item.id}
                  title={`Delete feedback from ${item.teacherName}'s ${item.squadName || 'Unnamed Squad'}`}
                  style={{
                    opacity: deletingId === item.id ? 0.6 : 1,
                    cursor: deletingId === item.id ? 'not-allowed' : 'pointer'
                  }}
                >
                  {deletingId === item.id ? '⏳' : '🗑️'}
                </button>
              </div>
            </div>
            <div className="feedback-card-body">
              <div className="feedback-grid">
                {Object.entries(item)
                  .filter(([key]) => !['id', 'squadId', 'sessionId', 'formVersion', 'squadName', 'createdAt', 'teacherId', 'teacherName'].includes(key))
                  .map(([key, value]) => (
                    <div key={key} className="feedback-item">
                      <h4>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</h4>
                      <div>{renderFieldValue(key, value)}</div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Custom Delete Confirmation Modal */}
      {showDeleteModal && pendingDelete && (
        <div className="feedback-modal-overlay" onClick={cancelDelete}>
          <div className="feedback-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="feedback-modal-header">
              <h2>🗑️ Confirm Deletion</h2>
              <button className="close-modal-btn" onClick={cancelDelete}>&times;</button>
            </div>
            <div className="feedback-modal-content">
              <div style={{ padding: '1.5rem 0', textAlign: 'center' }}>
                <div style={{ 
                  fontSize: '3rem', 
                  marginBottom: '1rem',
                  color: '#ef4444'
                }}>
                  ⚠️
                </div>
                <h3 style={{ 
                  color: '#1f2937', 
                  marginBottom: '1rem',
                  fontWeight: '600'
                }}>
                  Delete Feedback Response?
                </h3>
                <p style={{ 
                  color: '#6b7280', 
                  marginBottom: '1.5rem',
                  lineHeight: '1.6'
                }}>
                  Are you sure you want to delete the feedback response from{' '}
                  <strong style={{ color: '#1f2937' }}>
                    {pendingDelete.teacherName}'s {pendingDelete.squadName}
                  </strong>?
                </p>
                <div style={{
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  padding: '1rem',
                  color: '#dc2626',
                  fontWeight: '500'
                }}>
                  ⚠️ This action cannot be undone
                </div>
              </div>
            </div>
            <div className="feedback-modal-footer">
              <button 
                className="cancel-btn" 
                onClick={cancelDelete}
                style={{
                  background: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db'
                }}
              >
                Cancel
              </button>
              <button 
                className="submit-btn" 
                onClick={confirmDelete}
                style={{
                  background: '#ef4444',
                  color: 'white',
                  border: 'none'
                }}
              >
                🗑️ Delete Feedback
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackViewer; 